/**
 * The live assistant's own words, and the window's word for each thing the
 * server can answer.
 *
 * Read from `useLanguage()` inside the chat, the rule `strategyHelpCopy`
 * follows, and defaulted field by field against `en`, because a partial `t`
 * is what the harnesses hand the components rendered in the rail. The group
 * is flat strings; nothing in it is a plural form (a limit is quoted in
 * brackets, so no sentence turns on the number).
 *
 * The tables below are the copy table the window answers from: every refusal
 * the server sends before an answer, every error an answer can end with, and
 * every tool's status line, each with the way on it offers.
 * `assistant-chat-copy.test.ts` reads the server's codes and tool names and
 * fails one this page has no word for. A code a newer server sends that this
 * page does not know is worded generically rather than shown raw.
 */

import { en } from "../../i18n/en";
import { formatDateForDisplay } from "../../dateFormatting";
import type { AssistantMessageStatus, AssistantStopReason } from "../../services/assistantApi";
import { SUPPORT_EMAIL } from "./support-contact";

type AssistantChatKey = keyof typeof en.assistantChat;

export type AssistantChatCopy = Record<AssistantChatKey, string>;

export function assistantChatCopy(t: { assistantChat?: Partial<Record<AssistantChatKey, string>> }): AssistantChatCopy {
    const group = t.assistantChat ?? {};
    return Object.fromEntries(
        (Object.keys(en.assistantChat) as AssistantChatKey[]).map((key) => [key, group[key] ?? en.assistantChat[key]]),
    ) as AssistantChatCopy;
}

/**
 * The way on a problem offers: a new conversation (this one cannot take
 * another question), the question form (the assistant cannot answer today),
 * or handing the conversation to support (it could not answer this).
 */
export type AssistantProblemAction = "newConversation" | "askSupport" | "handOver" | null;

export interface AssistantProblem {
    text: string;
    action: AssistantProblemAction;
}

/** Every refusal the server sends before an answer, by code. */
export const ASSISTANT_REFUSAL_PROBLEMS: Readonly<Record<string, { key: AssistantChatKey; action: AssistantProblemAction }>> = {
    ASSISTANT_UNAVAILABLE: { key: "errorUnavailable", action: "askSupport" },
    ASSISTANT_MESSAGE_INVALID: { key: "errorInvalid", action: null },
    ASSISTANT_MESSAGE_PERSONAL_DATA: { key: "errorPersonalData", action: null },
    ASSISTANT_CONVERSATION_NOT_FOUND: { key: "errorConversationGone", action: "newConversation" },
    ASSISTANT_CONVERSATION_CLOSED: { key: "errorConversationClosed", action: "newConversation" },
    ASSISTANT_CONVERSATION_FULL: { key: "errorConversationFull", action: "newConversation" },
    ASSISTANT_CONVERSATION_EMPTY: { key: "errorHandOverEmpty", action: null },
    ASSISTANT_ALREADY_HANDED_OVER: { key: "errorHandOverAlready", action: "newConversation" },
    ASSISTANT_BUSY: { key: "errorBusy", action: null },
    ASSISTANT_LIMIT: { key: "errorLimit", action: "askSupport" },
    ASSISTANT_CONNECTION_REQUIRED: { key: "errorGeneric", action: null },
    ASSISTANT_OVERLOADED: { key: "errorOverloaded", action: null },
};

/** Every error an answer can end with, by code. */
export const ASSISTANT_STREAM_ERROR_PROBLEMS: Readonly<Record<string, { key: AssistantChatKey; action: AssistantProblemAction }>> = {
    ASSISTANT_FAILED: { key: "errorFailed", action: "handOver" },
    ASSISTANT_OVERLOADED: { key: "errorOverloaded", action: null },
    ASSISTANT_RATE_LIMITED: { key: "errorRateLimited", action: null },
    ASSISTANT_TIMEOUT: { key: "errorTimeout", action: null },
    SERVER_RESTARTING: { key: "errorRestarting", action: null },
};

export interface AssistantRefusal {
    code: string | null;
    /** The day's limit of questions, where that is what refused it. */
    limit?: number | null;
    maxMessageChars?: number;
}

/** A refused question in the operator's words, with the way on. */
export function assistantRefusalProblem(refusal: AssistantRefusal, copy: AssistantChatCopy): AssistantProblem {
    const entry = refusal.code ? ASSISTANT_REFUSAL_PROBLEMS[refusal.code] : undefined;
    if (!entry) return { text: copy.errorGeneric.replace("{email}", SUPPORT_EMAIL), action: null };
    const text = copy[entry.key]
        .replace("{limit}", String(refusal.limit ?? ""))
        .replace("{max}", String(refusal.maxMessageChars ?? 2000))
        .replace("{email}", SUPPORT_EMAIL);
    return { text, action: entry.action };
}

/** An answer that ended with an error, in the operator's words. */
export function assistantStreamErrorProblem(code: string | null, copy: AssistantChatCopy): AssistantProblem {
    const entry = (code ? ASSISTANT_STREAM_ERROR_PROBLEMS[code] : undefined) ?? ASSISTANT_STREAM_ERROR_PROBLEMS.ASSISTANT_FAILED;
    return { text: copy[entry.key], action: entry.action };
}

export interface AnswerOutcome {
    status: AssistantMessageStatus;
    stopReason: AssistantStopReason | null;
    text: string;
    /** Stopped by the operator in this window. */
    stopped?: boolean;
}

/** What is said under an answer about how it ended; null for one that ended well. */
export function assistantOutcomeNote(answer: AnswerOutcome, copy: AssistantChatCopy): string | null {
    if (answer.stopReason === "refusal") return copy.outcomeRefusal;
    if (answer.status === "streaming") return copy.outcomeStillWriting;
    if (answer.stopped) return copy.stopped;
    if (answer.stopReason === "max_tokens") return copy.outcomeMaxTokens;
    if (answer.stopReason === "iteration_limit") return copy.outcomeIterationLimit;
    if (answer.stopReason === "timeout") return copy.outcomeTimeout;
    if (answer.status === "failed" || (answer.status !== "complete" && answer.text.trim() === "")) return copy.outcomeFailed;
    if (answer.status === "interrupted") return copy.outcomeInterrupted;
    return null;
}

/** Each tool's status line: one without a night, and one naming the night it reads. */
export const ASSISTANT_TOOL_STATUS: Readonly<Record<string, { key: AssistantChatKey; dated?: AssistantChatKey }>> = {
    get_property_overview: { key: "toolPropertyOverview" },
    get_strategy_settings: { key: "toolStrategySettings" },
    get_strategy_changes: { key: "toolStrategyChanges" },
    get_prices: { key: "toolPrices", dated: "toolPricesFrom" },
    explain_night_price: { key: "statusThinking", dated: "toolExplainNight" },
    compare_nights: { key: "toolCompareNights" },
    get_night_history: { key: "statusThinking", dated: "toolNightHistory" },
    get_occupancy_and_pace: { key: "toolOccupancy", dated: "toolOccupancyFrom" },
    get_outlook: { key: "toolOutlook" },
    get_sync_and_uploads: { key: "toolSync" },
    get_analysis_summary: { key: "toolAnalysis" },
    get_market: { key: "toolMarket", dated: "toolMarketFrom" },
    get_recent_bookings_summary: { key: "toolBookings" },
    get_distribution_settings: { key: "toolDistribution" },
    get_linked_plans_night: { key: "toolDistribution", dated: "toolLinkedPlansNight" },
    what_if_price: { key: "toolWhatIf" },
    search_optivo_docs: { key: "toolDocs" },
    read_optivo_doc: { key: "toolDocs" },
    find_setting: { key: "toolFindSetting" },
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The status line for a tool at work. The subject is a night the server read
 * off the tool's input; anything that is not a calendar date is ignored, so a
 * subject can never put words on the screen.
 */
export function assistantToolStatus(tool: { name: string; subject: string | null }, copy: AssistantChatCopy, locale: string): string {
    const entry = ASSISTANT_TOOL_STATUS[tool.name];
    if (!entry) return copy.statusThinking;
    if (entry.dated && tool.subject && ISO_DATE.test(tool.subject)) {
        const date = formatDateForDisplay(tool.subject, locale, { day: "numeric", month: "long" });
        if (date && date !== "Invalid Date") return copy[entry.dated].replace("{date}", date);
    }
    return copy[entry.key];
}

const ANALYTICS_PAGES = new Set(["performance", "recentBookings", "forecast", "demandForecast", "segmentChannel", "marketConditions", "studyArchive"]);

/** The questions an empty conversation offers, by the page it is opened on. */
export function assistantSuggestions(page: string | null, copy: AssistantChatCopy): string[] {
    if (page === "drafts") return [copy.suggestPrices1, copy.suggestPrices2, copy.suggestPrices3];
    if (page === "strategies" || page === "onboarding") return [copy.suggestStrategy1, copy.suggestStrategy2, copy.suggestStrategy3];
    if (page === "dashboard") return [copy.suggestDashboard1, copy.suggestDashboard2, copy.suggestDashboard3];
    if (page && ANALYTICS_PAGES.has(page)) return [copy.suggestAnalytics1, copy.suggestAnalytics2, copy.suggestAnalytics3];
    if (page === "connections" || page === "settings") return [copy.suggestConnections1, copy.suggestConnections2, copy.suggestConnections3];
    return [copy.suggestGeneral1, copy.suggestGeneral2, copy.suggestGeneral3];
}
